import clsx from "clsx";
import { CSSProperties, FC, useState } from "react";

import { CopyButton, MarkdownReference } from "@tsmono/react/components";
import { isRecord } from "@tsmono/util";

import { copyValueText } from "./copyText";
import { isHtmlEscape } from "./htmlEscape";
import styles from "./MetaDataGrid.module.css";
import { RenderedContent } from "./RenderedContent";

interface MetadataGridProps {
  id?: string;
  className?: string | string[];
  references?: MarkdownReference[];
  style?: CSSProperties;
  entries: Record<string, unknown>;
  maxRows?: number;
  depth?: number;
  options?: {
    size?: "mini" | "small";
    plain?: boolean;
    striped?: boolean;
    previewRefsOnHover?: boolean;
    copyButton?: boolean;
  };
}

const isNonEmptyObject = (v: unknown): v is Record<string, unknown> =>
  isRecord(v) && Object.keys(v).length > 0;

/**
 * Renders structured metadata as a grid with section cards.
 *
 * Top-level scalars render as key-value rows; nested objects are
 * promoted to titled sections with a header rule. Recurses for
 * deeply nested objects.
 */
export const MetaDataGrid: FC<MetadataGridProps> = ({
  id,
  entries,
  className,
  references,
  style,
  maxRows,
  options,
  depth = 0,
}) => {
  const baseId = id ?? "metadata-grid";
  const allEntries = entryRecords(entries);

  // `{ _html: <ReactElement> }` escape hatches stay scalar rows so they
  // reach RenderedContent's Html branch instead of recursing into a group
  // that would dump the element as `$$typeof / type / props / ...`.
  const scalars = allEntries.filter(
    (e) => !isNonEmptyObject(e.value) || isHtmlEscape(e.value)
  );
  const groups = allEntries.filter(
    (e) => isNonEmptyObject(e.value) && !isHtmlEscape(e.value)
  );

  const [expanded, setExpanded] = useState(false);
  const isCollapsible = maxRows != null && scalars.length > maxRows;
  const visibleScalars =
    isCollapsible && !expanded ? scalars.slice(0, maxRows) : scalars;

  const depthClass =
    depth === 1 ? styles.depth1 : depth >= 2 ? styles.depth2 : undefined;

  return (
    <div
      id={depth === 0 ? id : undefined}
      className={clsx(
        depth === 0 && className,
        styles.grid,
        depthClass,
        options?.striped && depth === 0 && styles.striped
      )}
      style={depth === 0 ? style : undefined}
    >
      {visibleScalars.length > 0 && (
        <div
          className={clsx(styles.rows, options?.copyButton && styles.rowsCopy)}
        >
          {visibleScalars.map((entry, index) => {
            const entryId = `${baseId}-value-${index}`;
            return (
              <div key={entryId} className={styles.row}>
                <div className={styles.key}>{entry.name}</div>
                <div className={styles.val}>
                  <RenderedContent
                    id={entryId}
                    entry={entry}
                    references={references}
                    renderOptions={{
                      renderString: "markdown",
                      previewRefsOnHover: options?.previewRefsOnHover,
                    }}
                    renderObject={(obj: Record<string, unknown>) => (
                      <MetaDataGrid
                        id={entryId}
                        entries={obj}
                        options={options}
                        references={references}
                        depth={depth + 1}
                      />
                    )}
                  />
                </div>
                {options?.copyButton && !isHtmlEscape(entry.value) ? (
                  <div className={styles.copyCell}>
                    <CopyButton
                      value={copyValueText(entry.value)}
                      ariaLabel={`Copy ${entry.name}`}
                    />
                  </div>
                ) : undefined}
              </div>
            );
          })}
        </div>
      )}

      {isCollapsible && (
        <div className={styles.toggleContainer}>
          <button
            type="button"
            className={clsx(styles.toggleButton, "text-size-smallest")}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded
              ? "less"
              : `${scalars.length - visibleScalars.length} more`}
            ...
          </button>
        </div>
      )}

      {groups.map((entry, groupIndex) => {
        const groupId = `${baseId}-group-${groupIndex}`;
        return (
          <section
            key={groupId}
            className={clsx(styles.group, depth >= 1 && styles.depth1Group)}
          >
            <header className={styles.groupHeader}>
              <span className={styles.groupKey}>{entry.name}</span>
              <span className={styles.groupRule} />
            </header>
            <MetaDataGrid
              id={groupId}
              entries={isRecord(entry.value) ? entry.value : {}}
              options={options}
              references={references}
              depth={depth + 1}
            />
          </section>
        );
      })}
    </div>
  );
};

const entryRecords = (
  entries: { name: string; value: unknown }[] | Record<string, unknown>
): { name: string; value: unknown }[] => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!entries) {
    return [];
  }

  if (!Array.isArray(entries)) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return Object.entries(entries || {}).map(([key, value]) => {
      return { name: key, value };
    });
  } else {
    return entries;
  }
};
